import { FunctionComponent, useEffect, useState } from "react";
import { Button, IconButton, Checkbox, FormControlLabel, Dialog, Select } from '../../tailwindcomponents';
import { DatePicker } from '../../MaterialUiComponents';
import { Typography, Input, Table, TableCell, TableRow } from '../../tailwindcomponents';
import { Search, Check } from '../../components/icons';
import taskApi from '../../api/tasks';
import { useSelector } from "react-redux";
import { RootState } from "../../reducer";
import ChecklistApi from "../../api/checklists";
import { HangarChecklistDto } from "../../types/checklist";
import ChecklistDetailsModal from "./ChecklistDetailsModal";
import { DateTime } from "luxon";
import { TableColumns } from "../../types/table";

interface selectedVariables {
    tailNumber: string;
    description: string;
    status: boolean;
    inDate: string;
    outDate: string;
}

// Keys must match HangarChecklistDto exactly (PascalCase) - Table.tsx sorts on row[key],
// so a snake_case key resolves to undefined on every row and the header does nothing.
const sortTableColumns = [
    { label: "Created Date", key: "CreatedDate" },
    { label: "Tail Number", key: "TailNumber" },
    { label: "Description", key: "Description" },
    { label: "Active", key: "IsActive" },
    { label: "In Date", key: "InputDate" },
    { label: "Out Date", key: "OutputDate" },
    { label: "", key: "", sortable: false }
] as TableColumns[];

type ChecklistTemplateProps = {
    setLoading: Function;
    loading: boolean;
}

const ChecklistsOverview: FunctionComponent<ChecklistTemplateProps> = ({ setLoading, loading }) => {
    const store = useSelector((state: RootState) => state);
    const isChecklistAdmin = store.user.me.user_clearence_groups.includes("Checklist Edit");
    const [checklists, setChecklists] = useState<HangarChecklistDto[]>([]);
    const [filteredChecklists, setFilteredChecklists] = useState<HangarChecklistDto[]>([]);
    const [allTails, setAllTails] = useState<string[]>([]);

    const [selected, setSelected] = useState<selectedVariables>({ tailNumber: "", description: "", status: false, inDate: "", outDate: "" });
    const [selectedFilters, setSelectedFilters] = useState<selectedVariables>({ tailNumber: "", description: "", status: false, inDate: "", outDate: "" });

    const [selectedChecklist, setSelectedChecklist] = useState<HangarChecklistDto | undefined>(undefined);
    const [createChecklist, setCreateChecklist] = useState<boolean>(false);
    const [dialogText, setDialogText] = useState<string>("");

    const [checklistDialogOpen, setChecklistDialogOpen] = useState<boolean>(false);
    const [deleteChecklistDialogOpen, setDeleteChecklistDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        refresh();
    }, []);

    const refresh = () => {
        setLoading(true);
        setSelected(prevState => ({
            ...prevState,
            tailNumber: "",
            description: "",
            status: false,
        }));
        setCreateChecklist(false);
        taskApi.GetAllTailNumbers().then(res => {
            setAllTails(res.data);
            ChecklistApi.GetAllChecklists().then(res => {
                setChecklists(res.data);
                setLoading(false);
            });
        });
    }

    const selectChecklist = (checklist: HangarChecklistDto) => {
        const text = checklist.TailNumber + " " + checklist.Description;
        setDialogText(text);
        setSelectedChecklist(checklist);
        setChecklistDialogOpen(true);
    }

    const onDeleteChecklist = () => {
        setLoading(true);
        if (selectedChecklist === undefined) { return; }
        else {
            ChecklistApi.DeleteChecklist(selectedChecklist.Id).then(res => {
                setDeleteChecklistDialogOpen(false);
                refresh();
            });
        }
    }

    const generateTableBody = (row: HangarChecklistDto, index: number) => {
        return (
            <TableRow >
                <TableCell>{DateTime.fromISO(row.CreatedDate).toFormat('yyyy.MM.dd')}</TableCell>
                <TableCell>{row.TailNumber}</TableCell>
                <TableCell>{row.Description}</TableCell>
                <TableCell width="20">{row.IsActive === true ? <Check color="success" /> : ""}</TableCell>
                <TableCell>{DateTime.fromISO(row.InputDate).toFormat('yyyy.MM.dd HH:mm')}</TableCell>
                <TableCell>{DateTime.fromISO(row.OutputDate).toFormat('yyyy.MM.dd HH:mm')}</TableCell>
                <TableCell>{isChecklistAdmin && <IconButton onClick={() => selectChecklist(row)}><Search color="primary" /></IconButton>}</TableCell>
            </TableRow>
        )
    }

    const CreateChecklist = () => {
        setLoading(true);
        setCreateChecklist(false);
        ChecklistApi.CreateChecklist(selected.tailNumber, selected.description, selected.status, new Date(selected.inDate), new Date(selected.outDate)).then(res => {
            refresh();
        });
    }

    const ToLocalDateOnly = (dateStr: string): number => {
        const d = new Date(dateStr);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    }
    
    const applyFilters = () => {
        let filteredChecklists = checklists;
        if (selectedFilters.tailNumber !== "") {
            filteredChecklists = filteredChecklists.filter(x => x.TailNumber === selectedFilters.tailNumber);
        }
        if (selectedFilters.description !== "") {
            filteredChecklists = filteredChecklists.filter(x => x.Description === selectedFilters.description);
        }
        if (selectedFilters.inDate !== "" && selectedFilters.outDate !== "") {
            filteredChecklists = filteredChecklists.filter(x => {
              const inDate = ToLocalDateOnly(x.InputDate);
              const outDate = ToLocalDateOnly(x.OutputDate);
              const selectedIn = ToLocalDateOnly(selectedFilters.inDate);
              const selectedOut = ToLocalDateOnly(selectedFilters.outDate);
          
              return inDate >= selectedIn && outDate <= selectedOut;
            });
          }          
        if (selectedFilters.status !== undefined && selectedFilters.status !== null) {
            filteredChecklists = filteredChecklists.filter(x => x.IsActive === selectedFilters.status);
        }
        setFilteredChecklists(filteredChecklists);
    }

    const clearFilters = () => {
        setSelectedFilters(prevState => ({
            ...prevState,
            tailNumber: "",
            description: "",
            status: false,
            inDate: "",
            outDate: "",
        }))
        setFilteredChecklists([]);
    }

    return (
        <div>
            <Typography text={'Checklists'} variant='h1' align="center" />
            <div className="ds-filter-bar">
                <Select
                    width={100}
                    widthPercentage
                    filter_by_text="Tail number"
                    onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setSelectedFilters(prevState => ({
                            ...prevState,
                            tailNumber: e.target.value,
                        }))
                    }
                    options={allTails}
                    selectedItem={selectedFilters.tailNumber}
                />
                <Input
                    multiline={false}
                    help_text={""}
                    label_text={"Description"}
                    value={selectedFilters.description}
                    onTextChange={(text: string) =>
                        setSelectedFilters(prevState => ({
                            ...prevState,
                            description: text,
                        }))
                    }
                    type="text"
                    width={100}
                />
                <DatePicker
                    type="Date"
                    selected={selectedFilters.inDate ? new Date(selectedFilters.inDate) : null}
                    label="In Date"
                    onDateChange={(date: Date) => {
                        setSelectedFilters(prevState => ({
                            ...prevState,
                            inDate: date.toISOString(),
                        }))
                    }}
                />
                <DatePicker
                    type="Date"
                    selected={selectedFilters.outDate ? new Date(selectedFilters.outDate) : null}
                    label="Out Date"
                    onDateChange={(date: Date) => {
                        setSelectedFilters(prevState => ({
                            ...prevState,
                            outDate: date.toISOString(),
                        }))
                    }}
                />
                {/* Trailing actions share the grid so they sit on the same
                  * baseline as the filters. justify-start overrides the
                  * flex-end default of ds-filter-actions, which suits the split
                  * ds-filter-summary row rather than a group hugging the
                  * filters. Spanning two tracks from sm up keeps the checkbox
                  * and both buttons on one line; below that the grid is a
                  * single column, where a 2-track span would add an implicit
                  * column. */}
                <div className="ds-filter-actions justify-start sm:col-span-2">
                    <FormControlLabel
                        label="Active"
                        control={
                            <Checkbox
                                color="primary"
                                checked={selectedFilters.status}
                                onChange={() =>
                                    setSelectedFilters(prevState => ({
                                        ...prevState,
                                        status: !prevState.status,
                                    }))
                                }
                            />
                        }
                    />
                    <Button variant="contained" color="primary" size="small" onClick={() => applyFilters()}>
                        Apply Filters
                    </Button>
                    <Button variant="text" color="primary" size="small" onClick={() => clearFilters()}>
                        Clear Filters
                    </Button>
                </div>
            </div>
            {createChecklist &&
                <div className="flex flex-row flex-wrap justify-center mt-7">
                    <div className="w-1/12 md:w-1/12 lg:w-1/12 xl:w-1/12 mr-4">
                        <Select
                            width={100}
                            widthPercentage
                            filter_by_text="Tail number"
                            onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                setSelected(prevState => ({
                                    ...prevState,
                                    tailNumber: e.target.value,
                                }))
                            }
                            options={allTails}
                            selectedItem={selected.tailNumber}
                        />
                    </div>
                    <div className="w-1/4 md:w-1/4 lg:w-1/4 xl:w-1/4 mr-4">
                        <Input
                            multiline={false}
                            help_text={""}
                            label_text={"Description"}
                            value={selected.description}
                            onTextChange={(text: string) =>
                                setSelected(prevState => ({
                                    ...prevState,
                                    description: text,
                                }))
                            }
                            type="text"
                            width={100}
                        />
                    </div>
                    <div className="w-1/6 md:w-1/6 lg:w-1/6 xl:w-1/6 mr-4">
                        <DatePicker
                            type="Date"
                            selected={selected.inDate ? new Date(selected.inDate) : null}
                            label="Time"
                            onDateChange={(date: Date) => {
                                setSelected(prevState => ({
                                    ...prevState,
                                    inDate: date.toISOString(),
                                }))
                            }
                            }
                        />
                    </div>
                    <div className="w-1/6 md:w-1/6 lg:w-1/6 xl:w-1/6 mr-4">
                        <DatePicker
                            type="Date"
                            selected={selected.outDate ? new Date(selected.outDate) : null}
                            label="Time"
                            onDateChange={(date: Date) => {
                                setSelected(prevState => ({
                                    ...prevState,
                                    outDate: date.toISOString(),
                                }))
                            }
                            }
                        />
                    </div>

                    <div className="w-1/12 md:w-1/12 lg:w-1/12 xl:w-1/12 mt-2">
                        <Button variant="contained" color="primary" onClick={() => CreateChecklist()}>Save</Button>
                    </div>
                    <div className="w-1/12 md:w-1/12 lg:w-1/12 xl:w-1/12 mr-4 mt-2">
                        <Button variant="contained" color="secondary" onClick={() => setCreateChecklist(false)}>Abort</Button>
                    </div>
                </div>
            }
            <div>
                <Table
                    rows={(filteredChecklists.length !== 0) ? filteredChecklists : checklists}
                    generateBody={(row: HangarChecklistDto, index: number) => generateTableBody(row, index)}
                    pagination={true}
                    defaultRowsPerPage={10}
                    skipIndexCol={true}
                    sortTableColumns={sortTableColumns}
                />
            </div>
            <Dialog
                visible={checklistDialogOpen}
                max_width={"md"}
                title={dialogText}
                context={
                    <ChecklistDetailsModal
                        checklist={selectedChecklist}
                        checkListDialogOpen={setChecklistDialogOpen}
                        isChecklistAdmin={isChecklistAdmin}
                        refreshChecklists={refresh}
                        setLoading={setLoading}
                        loading={loading}
                    />
                }
                onClose={(status: Boolean) => setChecklistDialogOpen(false)}
                strict={false}
                show_options={false}
            />
            <Dialog
                visible={deleteChecklistDialogOpen}
                max_width={"sm"}
                title={"Are you sure you want to delete this checklist, it is not the same as marking it as done/inactive?"}
                context={<></>}
                onClose={(status: Boolean) => { status === true ? onDeleteChecklist() : setDeleteChecklistDialogOpen(false) }}
                strict={false}
                show_options={true}
            />
        </div>
    )
}

export default ChecklistsOverview;